import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../shared/utils.js/utils";

// Get Progress Bills
export const getProgressBills = createAsyncThunk(
  "progressBills/getProgressBills",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);
      if (params.contract_id)
        queryParams.append("contract_id", params.contract_id);
      if (params.project_id)
        queryParams.append("project_id", params.project_id);
      if (params.customer_id)
        queryParams.append("customer_id", params.customer_id);
      if (params.status) queryParams.append("status", params.status);
      if (params.type) queryParams.append("type", params.type);
      if (params.bill_category)
        queryParams.append("bill_category", params.bill_category);
      if (params.date_from) queryParams.append("date_from", params.date_from);
      if (params.date_to) queryParams.append("date_to", params.date_to);

      const url = `${BASE_URL}/progress-bills${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          Accept: "application/json",
        },
      });

      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get progress bills!";
      return rejectWithValue(message);
    }
  },
);

// Get Progress Bill Details
export const getProgressBillDetails = createAsyncThunk(
  "progressBills/getProgressBillDetails",
  async ({ id, language }, { rejectWithValue }) => {
    try {
      const token = getToken();
      // Use provided language or fallback to getCurrentLanguage
      const currentLang = language || getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.get(`${BASE_URL}/progress-bills/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          Accept: "application/json",
        },
      });

      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get progress bill details!";
      return rejectWithValue(message);
    }
  },
);

// Add Progress Bill from Contract
export const addProgressBill = createAsyncThunk(
  "progressBills/addProgressBill",
  async ({ contractId, progressBillData }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      // Check if we need FormData (has attachments or taxes)
      const hasAttachments =
        progressBillData.attachments &&
        Array.isArray(progressBillData.attachments) &&
        progressBillData.attachments.length > 0;
      const hasTaxes =
        progressBillData.taxes &&
        Array.isArray(progressBillData.taxes) &&
        progressBillData.taxes.length > 0;

      if (hasAttachments || hasTaxes) {
        const formData = new FormData();
        formData.append("date", progressBillData.date);
        formData.append("period_start", progressBillData.period_start);
        formData.append("period_end", progressBillData.period_end);
        if (progressBillData.notes) {
          formData.append("notes", progressBillData.notes);
        }
        if (progressBillData.bill_category) {
          formData.append("bill_category", progressBillData.bill_category);
        }
        if (progressBillData.type) {
          formData.append("type", progressBillData.type);
        }

        // Add taxes
        if (hasTaxes) {
          progressBillData.taxes.forEach(taxId => {
            formData.append("taxes[]", taxId);
          });
        }

        // Add items
        if (progressBillData.items && Array.isArray(progressBillData.items)) {
          progressBillData.items.forEach((item, index) => {
            formData.append(`items[${index}][id]`, item.id);
            formData.append(`items[${index}][current_qty]`, item.current_qty);
          });
        }

        // Add attachments
        if (hasAttachments) {
          progressBillData.attachments.forEach((attachment, index) => {
            if (attachment instanceof File) {
              formData.append(`attachments[${index}]`, attachment);
            }
          });
        }

        const response = await axios.post(
          `${BASE_URL}/progress-bills/from-contract/${contractId}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Accept-Language": currentLang,
              Accept: "application/json",
              "Content-Type": "multipart/form-data",
            },
          },
        );
        return response.data;
      } else {
        const response = await axios.post(
          `${BASE_URL}/progress-bills/from-contract/${contractId}`,
          progressBillData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Accept-Language": currentLang,
              Accept: "application/json",
            },
          },
        );
        return response.data;
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message =
        error.response?.data?.message || "Failed to add progress bill!";
      return rejectWithValue(message);
    }
  },
);

// Update Progress Bill
export const updateProgressBill = createAsyncThunk(
  "progressBills/updateProgressBill",
  async ({ id, progressBillData }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      // Use POST with _method: PUT for form data support
      const formData = new FormData();

      // Add _method: PUT
      formData.append("_method", "PUT");

      // Add items
      if (progressBillData.items && Array.isArray(progressBillData.items)) {
        progressBillData.items.forEach((item, index) => {
          formData.append(`items[${index}][id]`, item.id);
          formData.append(`items[${index}][current_qty]`, item.current_qty);
        });
      }

      // Add taxes
      if (progressBillData.taxes && Array.isArray(progressBillData.taxes)) {
        progressBillData.taxes.forEach(taxId => {
          formData.append("taxes[]", taxId);
        });
      }

      // Add notes
      if (progressBillData.notes !== undefined) {
        formData.append("notes", progressBillData.notes || "");
      }

      if (progressBillData.bill_category) {
        formData.append("bill_category", progressBillData.bill_category);
      }

      if (progressBillData.type) {
        formData.append("type", progressBillData.type);
      }

      if (progressBillData.invoice_number) {
        formData.append("invoice_number", progressBillData.invoice_number);
      }

      if (progressBillData.contract_id) {
        formData.append("contract_id", progressBillData.contract_id);
      }

      if (progressBillData.project_id) {
        formData.append("project_id", progressBillData.project_id);
      }

      // Add attachments
      if (
        progressBillData.attachments &&
        Array.isArray(progressBillData.attachments)
      ) {
        progressBillData.attachments.forEach((attachment, index) => {
          if (attachment instanceof File) {
            formData.append(`attachments[${index}]`, attachment);
          }
        });
      }

      const response = await axios.post(
        `${BASE_URL}/progress-bills/${id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
            Accept: "application/json",
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return response.data;
    } catch (error) {
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message =
        error.response?.data?.message || "Failed to update progress bill!";
      return rejectWithValue(message);
    }
  },
);

// Delete Progress Bill
export const deleteProgressBill = createAsyncThunk(
  "progressBills/deleteProgressBill",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.delete(`${BASE_URL}/progress-bills/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          Accept: "application/json",
        },
      });

      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete progress bill!";
      return rejectWithValue(message);
    }
  },
);

// Approve Progress Bill
export const approveProgressBill = createAsyncThunk(
  "progressBills/approveProgressBill",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.post(
        `${BASE_URL}/progress-bills/${id}/approve`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
            Accept: "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      if (
        error.response?.data?.errors &&
        typeof error.response.data.errors === "object"
      ) {
        const errorMessages = Object.values(error.response.data.errors)
          .flat()
          .filter(Boolean);
        if (errorMessages.length > 0) {
          return rejectWithValue(errorMessages.join(", "));
        }
      }
      const message =
        error.response?.data?.message || "Failed to approve progress bill!";
      return rejectWithValue(message);
    }
  },
);

// Get Progress Bill Journal Entries
export const getProgressBillJournalEntries = createAsyncThunk(
  "progressBills/getProgressBillJournalEntries",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.get(
        `${BASE_URL}/progress-bills/${id}/journal-entries`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
            Accept: "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get journal entries!";
      return rejectWithValue(message);
    }
  },
);
